import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    Input,
    OnChanges,
    OnDestroy,
    SimpleChanges,
    ViewChild,
} from '@angular/core';
import { GISService } from 'app/shared/services/gis-service';
import { BaseMapListItem } from 'app/shared/models/gis-service-list';
import { darkThemeColors, lightThemeColors, monitorLocation } from 'app/shared/constant/mapStyle.constant';
import { LocationStatus } from 'app/shared/models/locations';
import { configureBasemapTileLayer, createBasemapTileLoadFunction } from 'app/shared/models/map-view';
import Feature from 'ol/Feature';
import { EventsKey } from 'ol/events';
import Point from 'ol/geom/Point';
import VectorLayer from 'ol/layer/Vector';
import TileLayer from 'ol/layer/Tile';
import OLMap from 'ol/Map';
import View from 'ol/View';
import { defaults as defaultControls } from 'ol/control';
import { fromLonLat } from 'ol/proj';
import { unByKey } from 'ol/Observable';
import VectorSource from 'ol/source/Vector';
import XYZ from 'ol/source/XYZ';
import { Style } from 'ol/style';
import { catchError, takeUntil } from 'rxjs/operators';
import { forkJoin, of, Subject } from 'rxjs';
import { apply } from 'ol-mapbox-style';

@Component({
    selector: 'app-flex-static-map',
    templateUrl: './flex-static-map.component.html',
    styleUrls: ['./flex-static-map.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FlexStaticMapComponent implements AfterViewInit, OnChanges, OnDestroy {
    @ViewChild('mapTarget') private mapTarget: ElementRef<HTMLDivElement>;

    @Input() public latitude: number | null = null;
    @Input() public longitude: number | null = null;
    @Input() public locationName = '';
    @Input() public unavailableText = '';
    @Input() public ariaLabel = '';
    @Input() public isDarkTheme = false;
    @Input() public retryToken = '';
    @Input() public locationStatus: number | null = null;

    public isLoading = false;
    public hasError = false;

    private map: OLMap | null = null;
    private markerLayer: VectorLayer | null = null;
    private markerSource = new VectorSource();
    private resizeObserver: ResizeObserver | null = null;
    private mapEventKeys: EventsKey[] = [];
    private setupVersion = 0;
    private viewInitialized = false;
    private readonly destroy$ = new Subject<void>();
    private readonly printResizeHandler = (): void => {
        this.map?.updateSize();
        this.map?.renderSync();
    };

    constructor(private gisService: GISService, private cdr: ChangeDetectorRef) {}

    public get hasCoordinates(): boolean {
        return (
            this.latitude !== null &&
            this.longitude !== null &&
            Number.isFinite(this.latitude) &&
            Number.isFinite(this.longitude) &&
            this.latitude >= -90 &&
            this.latitude <= 90 &&
            this.longitude >= -180 &&
            this.longitude <= 180
        );
    }

    public ngAfterViewInit(): void {
        this.viewInitialized = true;
        if (typeof ResizeObserver !== 'undefined') {
            this.resizeObserver = new ResizeObserver(() => this.map?.updateSize());
            this.resizeObserver.observe(this.mapTarget.nativeElement);
        }
        this.gisService.gisUserSettingsSubject$.pipe(takeUntil(this.destroy$)).subscribe(() => {
            this.updateMarkerStyle();
        });
        window.addEventListener('beforeprint', this.printResizeHandler);
        window.addEventListener('afterprint', this.printResizeHandler);
        this.initializeMap();
    }

    public ngOnChanges(changes: SimpleChanges): void {
        if (!this.viewInitialized) return;
        if (changes.latitude || changes.longitude) this.initializeMap();
        if (changes.isDarkTheme || changes.locationStatus) {
            this.updateMarkerStyle();
            this.map?.render();
        }
        if (changes.retryToken && this.hasError) this.retry();
        this.map?.updateSize();
    }

    public ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
        window.removeEventListener('beforeprint', this.printResizeHandler);
        window.removeEventListener('afterprint', this.printResizeHandler);
        this.resizeObserver?.disconnect();
        this.resizeObserver = null;
        this.disposeMap();
    }

    public retry(): void {
        if (this.isLoading) return;
        this.disposeMap();
        this.initializeMap();
    }

    private initializeMap(): void {
        if (!this.hasCoordinates || !this.mapTarget) {
            this.disposeMap();
            this.isLoading = false;
            this.hasError = false;
            return;
        }

        if (this.map) {
            this.updateMarker();
            return;
        }

        const latitude = this.latitude;
        const longitude = this.longitude;
        if (latitude === null || longitude === null) return;

        this.isLoading = true;
        this.hasError = false;
        const setupVersion = ++this.setupVersion;
        forkJoin({
            token: this.gisService.getGisToken().pipe(catchError(() => of(null))),
            basemaps: this.gisService.getGISBasemap().pipe(catchError(() => of([]))),
        })
            .pipe(takeUntil(this.destroy$))
            .subscribe(({ token, basemaps }) => {
                if (setupVersion !== this.setupVersion) return;

                const basemap = selectBasemap(basemaps);
                const requiresToken = basemap?.url.includes('{token}') ?? false;
                if (!basemap || (requiresToken && !token?.access_token)) {
                    this.showError(setupVersion);
                    return;
                }

                try {
                    const view = new View({ center: fromLonLat([longitude, latitude]), zoom: 17 });
                    const map = new OLMap({
                        target: this.mapTarget.nativeElement,
                        view,
                        layers: [],
                        interactions: [],
                        controls: defaultControls({ zoom: false, rotate: false, attribution: true }),
                    });
                    this.map = map;

                    const url = token?.access_token ? basemap.url.replace('{token}', token.access_token) : basemap.url;
                    if (basemap.url.includes('token')) {
                        this.applyDynamicBasemap(map, url, basemap.name, setupVersion);
                        return;
                    }

                    map.addLayer(
                        new TileLayer({
                            name: basemap.name,
                            source: new XYZ({
                                url,
                                crossOrigin: 'Anonymous',
                                tileLoadFunction: createBasemapTileLoadFunction(map),
                            }),
                        }),
                    );
                    this.completeMapSetup(map, setupVersion);
                } catch {
                    this.showError(setupVersion);
                }
            });
    }

    private applyDynamicBasemap(map: OLMap, url: string, name: string, setupVersion: number): void {
        const layerAddKey = map.getLayers().on('add', (event) => {
            if (event.element instanceof TileLayer) {
                this.mapEventKeys.push(...configureBasemapTileLayer(event.element));
            }
        });
        this.mapEventKeys.push(layerAddKey);

        apply(map, url)
            .then((result) => {
                if (setupVersion !== this.setupVersion || result !== this.map) return;

                result
                    .getLayers()
                    .getArray()
                    .filter((layer) => layer.get('name') === undefined)
                    .forEach((layer) => layer.set('name', name));
                this.completeMapSetup(result, setupVersion);
            })
            .catch(() => this.showError(setupVersion))
            .finally(() => this.removeMapEventKey(layerAddKey));
    }

    private completeMapSetup(map: OLMap, setupVersion: number): void {
        if (setupVersion !== this.setupVersion || map !== this.map) return;

        this.markerLayer = new VectorLayer({
            name: 'FLEX Monitor Status Location',
            source: this.markerSource,
            zIndex: 2,
        });
        map.addLayer(this.markerLayer);
        this.updateMarker();
        this.isLoading = false;
        this.hasError = false;
        this.cdr.markForCheck();
    }

    private showError(setupVersion: number): void {
        if (setupVersion !== this.setupVersion) return;

        this.disposeMap();
        this.isLoading = false;
        this.hasError = true;
        this.cdr.markForCheck();
    }

    private removeMapEventKey(key: EventsKey): void {
        unByKey(key);
        this.mapEventKeys = this.mapEventKeys.filter((eventKey) => eventKey !== key);
    }

    private disposeMap(): void {
        this.setupVersion += 1;
        unByKey(this.mapEventKeys);
        this.mapEventKeys = [];
        this.map?.setTarget(undefined);
        this.map = null;
        this.markerLayer = null;
    }

    private createMarkerStyle(feature: Feature): Style[] {
        if (!this.gisService.gisUserSettingsSubject$.getValue()) return [];

        const isActive =
            this.locationStatus === null ||
            this.locationStatus === LocationStatus.Active ||
            this.locationStatus === LocationStatus.Maintenance;
        const color = isActive
            ? this.isDarkTheme
                ? darkThemeColors.green
                : lightThemeColors.green
            : this.isDarkTheme
            ? darkThemeColors.grey
            : lightThemeColors.grey;

        return monitorLocation(feature, color, this.gisService.gisUserSettingsSubject$, null);
    }

    private updateMarker(): void {
        const latitude = this.latitude;
        const longitude = this.longitude;
        if (!this.map || !this.hasCoordinates || latitude === null || longitude === null) return;

        const coordinate = fromLonLat([longitude, latitude]);
        this.markerSource.clear();
        const marker = new Feature({
            geometry: new Point(coordinate),
            Name: this.locationName,
            LocationID: null,
            IsActiveLocation: this.locationStatus === LocationStatus.Active,
            Status: this.locationStatus,
            monitorSeriesModelName: 'FLEX',
        });
        this.markerSource.addFeature(marker);
        this.updateMarkerStyle();
        this.map.getView().setCenter(coordinate);
        this.map.getView().setZoom(17);
        this.map.updateSize();
    }

    private updateMarkerStyle(): void {
        const marker = this.markerSource.getFeatures()[0];
        if (!this.markerLayer || !marker) return;

        this.markerLayer.setStyle(this.createMarkerStyle(marker));
    }
}

function selectBasemap(basemaps: BaseMapListItem[]): BaseMapListItem | null {
    return basemaps.find((basemap) => basemap.isActive) ?? basemaps[0] ?? null;
}
